import React, { useMemo, useState, useEffect } from "react";
import "./index.scss";
import { TabBarNav } from "@shared/components/TabBar/TabBarNav";
import classNames from "classnames";
import { Button } from "@shared/components";
import { useSearchParams } from "react-router-dom";

export interface TabBar {
  className?: string;
  children: {
    props: {
      label: string;
      isAccess?: boolean;
      // Phase 42: an optional per-tab count, e.g. the Review tab's pending-recognitions count.
      // Additive -- every existing consumer that never sets it renders byte-identical to before.
      badge?: number;
      actionButtonTitle?: string;
      actionButtonOnClick?: () => void;
      // Per-tab, like actionButtonTitle/actionButtonOnClick above — defaults to "clear" (today's
      // hardcoded style) in renderActionButton() below, so the two existing UserViewContainer
      // action buttons that never set this render byte-identical to before.
      actionButtonClassName?: string;
    };
  }[];
  onChangeActiveTabHandler?: (label: string, number: number) => void;
  isLoading?: boolean;
  activeTab?: number;
  // Defaults to "activeTab" so every existing consumer (there are two, app-wide) is byte-for-byte
  // unchanged. A consumer that nests inside another TabBar-driven screen — like the outer
  // Recognitions tab bar sitting above the existing inner Received/Sent bar — must pass its own
  // name here, or a click on either bar silently moves the other (both would write "activeTab").
  searchParamName?: string;
}

const TabBar: React.FC<TabBar> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { children, className, onChangeActiveTabHandler, isLoading, searchParamName = "activeTab" } = props;
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setActiveTab(props.activeTab ? props.activeTab : 0);
  }, [props.activeTab]);

  const activeChildren = useMemo(
    () => children?.filter(({ props }) => (props.isAccess === undefined ? true : props.isAccess)),
    [children],
  );

  const labelsList = useMemo(() => activeChildren?.map(({ props }) => props.label), [activeChildren]);

  const onChangeActiveTab = (label: string) => {
    const activeTabNum = labelsList.indexOf(label);
    setActiveTab(activeTabNum);
    searchParams.set(searchParamName, String(activeTabNum));
    setSearchParams(searchParams);
    if (onChangeActiveTabHandler) {
      onChangeActiveTabHandler(label, labelsList.indexOf(label));
    }
  };

  const renderTabs = () =>
    labelsList.map((label: string, key: number) => (
      <TabBarNav
        label={label}
        // Phase 42: the index, not the label. Two existing consumers put a changing count INSIDE
        // their label text (RecognitionList.tsx:137,157 -- `Received (${total})`, `Sent (${total})`),
        // so `key={label}` remounted those tabs' content on every count change; an index key has no
        // such dependency. Not a consequence of the badge: D1 keeps the count OUT of the label (see
        // the `badge` prop above), so a badged tab's label is as stable a key as it ever was.
        key={key}
        badge={activeChildren[key]?.props.badge}
        className={key === activeTab ? "active-tab" : ""}
        onChangeActiveTab={onChangeActiveTab}
      />
    ));

  const renderContent = () => {
    const activeChild = activeChildren[activeTab];
    return activeChild ? (activeChild as React.ReactNode) : null;
  };

  const renderActionButton = () => {
    // Defensive guard: an out-of-range activeTab (e.g. a stale/unknown deep-link value a consumer
    // failed to clamp) must render nothing here, not throw — this protects every consumer,
    // including RecognitionsTabBarContainer and UserViewContainer.
    const activeChild = activeChildren[activeTab];
    if (!activeChild) {
      return null;
    }
    const { props } = activeChild;
    return props.actionButtonTitle && props.actionButtonOnClick ? (
      <Button
        type="button"
        className={classNames("margin-right", props.actionButtonClassName ?? "clear")}
        onClick={() => props.actionButtonOnClick?.()}
      >
        {props.actionButtonTitle}
      </Button>
    ) : null;
  };

  const classes = classNames("tab-bar", className);

  return (
    <div className={classes}>
      <div className="tab-bar-nav">
        <div className="tab-bar-nav-left">{renderTabs()}</div>
        <div className="tab-bar-nav-right">{renderActionButton()}</div>
      </div>
      <div className="tab-container">{!isLoading ? renderContent() : null}</div>
    </div>
  );
};

export default TabBar;
